import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useAssessment } from '../hooks/useAssessment';
import Button from '../components/Shared/Button';
import ProgressBar from '../components/Assessment/ProgressBar';
import QuestionCard from '../components/Assessment/QuestionCard';
import { theme } from '../styles/theme';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeaderSection = styled.div`
  margin-bottom: ${theme.spacing.xlarge};
`;

const DimensionTitle = styled.h2`
  font-family: ${theme.fonts.heading};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.small};
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.large};
`;

const Assessment = () => {
  const history = useHistory();
  const { 
    dimensions, 
    answers, 
    setAnswer, 
    answeredCount, 
    totalQuestions,
    setIsComplete 
  } = useAssessment();

  // State to track current position
  const [currentDimensionIndex, setCurrentDimensionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Flatten logic could be handled differently, but let's iterate dimensions -> questions
  const currentDimension = dimensions[currentDimensionIndex];
  const currentQuestion = currentDimension?.questions[currentQuestionIndex];

  // Derive absolute index for progress
  const absoluteIndex = dimensions.slice(0, currentDimensionIndex).reduce((acc, dim) => acc + dim.questions.length, 0) + currentQuestionIndex;

  const handleSelect = (score: number) => {
    if (currentQuestion) {
      setAnswer(currentQuestion.id, score);
    }
  };

  const handleNext = () => {
    // If more questions in this dimension
    if (currentQuestionIndex < currentDimension.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // If more dimensions
      if (currentDimensionIndex < dimensions.length - 1) {
        setCurrentDimensionIndex(prev => prev + 1);
        setCurrentQuestionIndex(0);
      } else {
        // Finished
        setIsComplete(true);
        history.push('/results');
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      if (currentDimensionIndex > 0) {
        const prevDimensionIndex = currentDimensionIndex - 1;
        setCurrentDimensionIndex(prevDimensionIndex);
        setCurrentQuestionIndex(dimensions[prevDimensionIndex].questions.length - 1);
      }
    }
  };

  // Ensure answer exists for current question to enable Next
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const canProceed = currentAnswer !== undefined;

  useEffect(() => {
     window.scrollTo(0, 0);
  }, [currentQuestion]);

  if (!currentQuestion) return <div>Loading...</div>;

  return (
    <PageContainer>
      <HeaderSection>
        <ProgressBar current={absoluteIndex + 1} total={totalQuestions} />
        <div style={{ marginTop: '20px' }}>
             <small style={{ textTransform: 'uppercase', color: theme.colors.secondary, letterSpacing: '1px' }}>
                Part {currentDimensionIndex + 1} of {dimensions.length}
            </small>
            <DimensionTitle>{currentDimension.name}</DimensionTitle>
            <p>{currentDimension.description}</p>
        </div>
      </HeaderSection>
      
      <QuestionCard 
        question={currentQuestion}
        selectedScore={currentAnswer}
        onSelect={handleSelect}
      />

      <Controls>
        <Button 
            variant="outline" 
            onClick={handlePrevious} 
            disabled={absoluteIndex === 0}
        >
          Previous
        </Button>
        <Button 
            onClick={handleNext} 
            disabled={!canProceed}
        >
          {absoluteIndex === totalQuestions - 1 ? 'Finish Assessment' : 'Next Question'}
        </Button>
      </Controls>
    </PageContainer>
  );
};

export default Assessment;